import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/contexts/ProfileContext';
import { Message, ReplyingToMessage } from '@/types/chat';
import { toast } from '@/hooks/use-toast';
import { debugLogger } from '@/utils/debugLogger';
import { businessRulesEngine } from '@/core/BusinessRulesEngine';
import { useQueryClient } from '@tanstack/react-query';

interface Partner {
  id: string;
  username: string;
  avatar?: string;
  is_online: boolean;
  last_seen?: string;
  verification_level?: string;
}

interface Conversation {
  id: string;
  deleted_at: string | null;
  deleted_by: string | null;
  unread_count: number;
}

interface ChatData {
  conversation: Conversation;
  partner: Partner;
  messages: Message[];
  media_messages: Array<{
    id: string;
    media_url: string;
    created_at: string;
  }>;
  has_older_messages: boolean;
}

/**
 * 🚀 OPTIMIZED CHAT HOOK
 * Reduces 19 requests to 3-4 using specialized RPCs
 * - get_chat_data: 13 queries → 1 query
 * - mark_chat_as_read: 4 queries → 1 query
 * - load_older_messages: 2 queries → 1 query
 */
export const useOptimizedChat = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile: userProfile } = useProfile();
  const queryClient = useQueryClient();
  
  const [chatData, setChatData] = useState<ChatData | null>(null);
  const [loading, setLoading] = useState(true);
  const [replyingTo, setReplyingTo] = useState<ReplyingToMessage | null>(null);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
  const [isReactivating, setIsReactivating] = useState(false);
  
  const channelRef = useRef<any>(null);

  // 🚀 SINGLE QUERY: Fetch ALL chat data at once
  const initializeChat = useCallback(async () => {
    if (!user?.id || !username) return;

    try {
      debugLogger.debug('chat', '🚀 Fetching ALL chat data with SINGLE RPC call');
      const startTime = performance.now();
      
      const { data, error } = await supabase.rpc('get_chat_data', {
        p_partner_username: username
      });

      const elapsed = performance.now() - startTime;
      debugLogger.debug('chat', `✅ Chat data loaded in ${elapsed.toFixed(0)}ms`);

      if (error) throw error;

      // Type assertion for RPC response
      const chatResult = data as any;

      if (chatResult?.error) {
        debugLogger.error('chat', 'Partner not found:', username);
        navigate('/main/chats');
        return;
      }

      // Reverse messages to chronological order (oldest first)
      const reversedMessages = [...(chatResult.messages || [])].reverse();
      
      setChatData({
        conversation: chatResult.conversation,
        partner: chatResult.partner,
        messages: reversedMessages,
        media_messages: chatResult.media_messages || [],
        has_older_messages: chatResult.has_older_messages || false
      });

      // 🚀 SINGLE QUERY: Mark as read (non-blocking, parallel)
      if (chatResult.conversation?.id && chatResult.conversation.unread_count > 0) {
        supabase.rpc('mark_chat_as_read', {
          p_conversation_id: chatResult.conversation.id
        }).then(() => {
          debugLogger.debug('chat', 'Messages marked as read');
          queryClient.invalidateQueries({ queryKey: ['conversations', user.id] });
        });
      }

      setLoading(false);
    } catch (error) {
      debugLogger.error('chat', 'Error initializing chat:', error);
      toast({
        title: 'Erro ao carregar chat',
        description: 'Tente novamente.',
        variant: 'destructive'
      });
      setLoading(false);
    }
  }, [user?.id, username, navigate, queryClient]);

  // 🚀 OPTIMIZED: Load older messages with pagination
  const loadOlderMessages = useCallback(async () => {
    if (!chatData?.conversation?.id || !chatData.messages.length || loadingOlderMessages) return;

    setLoadingOlderMessages(true);
    try {
      const oldestMessage = chatData.messages[0];
      debugLogger.debug('chat', 'Loading older messages before:', oldestMessage.created_at);
      
      const { data, error } = await supabase.rpc('load_older_messages', {
        p_conversation_id: chatData.conversation.id,
        p_before_timestamp: oldestMessage.created_at,
        p_limit: 20
      });

      if (error) throw error;

      // Type assertion for RPC response
      const result = data as any;

      if (result.messages && result.messages.length > 0) {
        const reversedOlderMessages = [...result.messages].reverse();
        debugLogger.info('chat', `Loaded ${result.messages.length} older messages`);
        
        setChatData(prev => ({
          ...prev!,
          messages: [...reversedOlderMessages, ...prev!.messages],
          has_older_messages: result.has_more || false
        }));
      }
    } catch (error) {
      debugLogger.error('chat', 'Error loading older messages:', error);
    } finally {
      setLoadingOlderMessages(false);
    }
  }, [chatData, loadingOlderMessages]);

  // Setup realtime subscription
  const setupRealtime = useCallback(() => {
    if (!chatData?.conversation?.id) return;

    debugLogger.debug('chat', 'Setting up realtime for conversation:', chatData.conversation.id);

    const channel = supabase
      .channel(`messages:${chatData.conversation.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${chatData.conversation.id}`
        },
        (payload) => {
          const newMessage = payload.new as Message;
          debugLogger.debug('chat', 'New message received:', newMessage.id);
          
          setChatData(prev => {
            if (!prev) return prev;
            const exists = prev.messages.some(m => m.id === newMessage.id);
            if (exists) return prev;
            
            return {
              ...prev,
              messages: [...prev.messages, newMessage]
            };
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${chatData.conversation.id}`
        },
        (payload) => {
          const updatedMessage = payload.new as Message;
          
          setChatData(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              messages: prev.messages.map(m => 
                m.id === updatedMessage.id ? updatedMessage : m
              )
            };
          });
        }
      )
      .subscribe();

    channelRef.current = channel;
  }, [chatData?.conversation?.id]);

  const validateMessageLimit = useCallback(async () => {
    if (!user?.id || !userProfile) return false;

    try {
      const today = new Date().toISOString().split('T')[0];
      const { data: messages } = await supabase
        .from('messages')
        .select('id', { count: 'exact' })
        .eq('sender_id', user.id)
        .gte('created_at', `${today}T00:00:00.000Z`)
        .lt('created_at', `${today}T23:59:59.999Z`);

      const todayCount = messages?.length || 0;

      const result = await businessRulesEngine.validateInteractionLimits(
        {
          id: user.id,
          plan_type: userProfile.plan_type || 'sponsor_free',
          role: userProfile.role,
          hidden: userProfile.hidden || false,
          verification_level: 'basic'
        },
        'daily_messages',
        todayCount
      );

      return result.allowed;
    } catch (error) {
      debugLogger.error('chat', 'Error validating message limit:', error);
      return false;
    }
  }, [user?.id, userProfile]);

  const sendText = useCallback(async (content: string): Promise<boolean> => {
    if (!chatData?.conversation?.id || !user?.id || !content.trim()) return false;

    const canSend = await validateMessageLimit();
    if (!canSend) {
      debugLogger.warn('chat', 'Message blocked due to daily limit');
      return false;
    }

    try {
      const messageData: any = {
        conversation_id: chatData.conversation.id,
        sender_id: user.id,
        content: content.trim(),
        content_type: 'text'
      };

      if (replyingTo) {
        messageData.reply_to_message_id = replyingTo.id;
      }

      const { error } = await supabase
        .from('messages')
        .insert([messageData]);

      if (error) throw error;

      setReplyingTo(null);
      return true;
    } catch (error) {
      debugLogger.error('chat', 'Error sending text:', error);
      toast({
        title: 'Erro ao enviar mensagem',
        description: 'Tente novamente.',
        variant: 'destructive'
      });
      return false;
    }
  }, [chatData?.conversation?.id, user?.id, replyingTo, validateMessageLimit]);

  const sendImage = useCallback(async (file: File): Promise<boolean> => {
    if (!chatData?.conversation?.id || !user?.id) return false;

    const canSend = await validateMessageLimit();
    if (!canSend) {
      debugLogger.warn('chat', 'Image blocked due to daily limit');
      return false;
    }

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${user.id}.${fileExt}`;
      const filePath = `conversations/${chatData.conversation.id}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('chat_media')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('chat_media')
        .getPublicUrl(filePath);

      const messageData: any = {
        conversation_id: chatData.conversation.id,
        sender_id: user.id,
        content: 'Imagem',
        content_type: 'image',
        media_url: data.publicUrl
      };

      if (replyingTo) {
        messageData.reply_to_message_id = replyingTo.id;
      }

      const { error } = await supabase
        .from('messages')
        .insert([messageData]);

      if (error) throw error;

      setReplyingTo(null);
      return true;
    } catch (error) {
      debugLogger.error('chat', 'Error sending image:', error);
      toast({
        title: 'Erro ao enviar imagem',
        description: 'Tente novamente.',
        variant: 'destructive'
      });
      return false;
    }
  }, [chatData?.conversation?.id, user?.id, replyingTo, validateMessageLimit]);

  const sendAudio = useCallback(async (audioBlob: Blob, duration: number): Promise<boolean> => {
    if (!chatData?.conversation?.id || !user?.id) return false;

    const canSend = await validateMessageLimit();
    if (!canSend) {
      debugLogger.warn('chat', 'Audio blocked due to daily limit');
      return false;
    }

    try {
      const fileName = `${Date.now()}-${user.id}.webm`;
      const filePath = `conversations/${chatData.conversation.id}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('chat_media')
        .upload(filePath, audioBlob);

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('chat_media')
        .getPublicUrl(filePath);

      const messageData: any = {
        conversation_id: chatData.conversation.id,
        sender_id: user.id,
        content: 'Áudio',
        content_type: 'audio',
        media_url: data.publicUrl,
        audio_duration: duration
      };

      if (replyingTo) {
        messageData.reply_to_message_id = replyingTo.id;
      }

      const { error } = await supabase
        .from('messages')
        .insert([messageData]);

      if (error) throw error;

      setReplyingTo(null);
      return true;
    } catch (error) {
      debugLogger.error('chat', 'Error sending audio:', error);
      toast({
        title: 'Erro ao enviar áudio',
        description: 'Tente novamente.',
        variant: 'destructive'
      });
      return false;
    }
  }, [chatData?.conversation?.id, user?.id, replyingTo, validateMessageLimit]);

  const deleteMessage = useCallback(async (messageId: string): Promise<boolean> => {
    if (!chatData?.conversation?.id || !user?.id) return false;

    try {
      const { error } = await supabase
        .from('messages')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', messageId)
        .eq('sender_id', user.id);

      if (error) throw error;
      return true;
    } catch (error) {
      debugLogger.error('chat', 'Error deleting message:', error);
      return false;
    }
  }, [chatData?.conversation?.id, user?.id]);

  const deleteConversation = useCallback(async (): Promise<boolean> => {
    if (!chatData?.conversation?.id || !user?.id) return false;

    try {
      const { error } = await supabase
        .from('conversations')
        .update({
          deleted_at: new Date().toISOString(),
          deleted_by: user.id
        })
        .eq('id', chatData.conversation.id);

      if (error) throw error;

      navigate('/main/chats');
      return true;
    } catch (error) {
      debugLogger.error('chat', 'Error deleting conversation:', error);
      return false;
    }
  }, [chatData?.conversation?.id, user?.id, navigate]);

  const reactivateConversation = useCallback(async (): Promise<boolean> => {
    if (!chatData?.conversation?.id || !user?.id) return false;

    setIsReactivating(true);
    try {
      const { error } = await supabase
        .from('conversations')
        .update({
          deleted_at: null,
          deleted_by: null
        })
        .eq('id', chatData.conversation.id);

      if (error) throw error;

      setChatData(prev => prev ? {
        ...prev,
        conversation: {
          ...prev.conversation,
          deleted_at: null,
          deleted_by: null
        }
      } : null);

      return true;
    } catch (error) {
      debugLogger.error('chat', 'Error reactivating conversation:', error);
      return false;
    } finally {
      setIsReactivating(false);
    }
  }, [chatData?.conversation?.id, user?.id]);

  // Initialize chat on mount
  useEffect(() => {
    initializeChat();
  }, [initializeChat]);

  // Setup realtime when conversation is ready
  useEffect(() => {
    if (chatData?.conversation?.id) {
      setupRealtime();
    }
    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
    };
  }, [chatData?.conversation?.id, setupRealtime]);

  return {
    // Data
    conversation: chatData?.conversation || null,
    messages: chatData?.messages || [],
    partner: chatData?.partner || null,
    mediaMessages: chatData?.media_messages || [],
    hasOlderMessages: chatData?.has_older_messages || false,
    
    // States
    loading,
    loadingOlderMessages,
    isReactivating,
    replyingTo,
    
    // Actions
    sendText,
    sendImage,
    sendAudio,
    deleteMessage,
    deleteConversation,
    reactivateConversation,
    loadOlderMessages,
    setReplyingTo
  };
};
